import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Trash2, Plus, ImagePlus, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const fmtError = (d) => {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || "Something went wrong.";
};

const CATEGORIES = ["CCTV & Surveillance", "Networking", "Access Control", "Biometric & Attendance", "EPABX & Communication", "Fire Alarm Systems", "Public Address Systems", "Structured Cabling", "Network & Server Infrastructure", "IT Hardware", "Power & UPS", "Software & Licenses", "Computers & Laptops", "Servers & Storage", "Printers & Scanners", "Displays & Signage", "Peripherals & Accessories", "Services", "Other"];

export const AdminProducts = ({ onAuthFail }) => {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", category: CATEGORIES[0], price_inr: "", description: "" });
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/products");
      setProducts(data);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else toast.error("Could not load products");
    }
  }, [onAuthFail]);

  useEffect(() => { load(); }, [load]);

  const pickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose a JPG or PNG image.");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be smaller than 5 MB.");
    setImage(file);
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = new FormData();
      payload.append("name", form.name.trim());
      payload.append("category", form.category);
      payload.append("price_inr", form.price_inr === "" ? "0" : String(parseInt(form.price_inr, 10)));
      payload.append("description", form.description.trim());
      payload.append("in_stock", "true");
      if (image) payload.append("image", image);
      await api.post("/products", payload, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${form.name.trim()} is live on the store`);
      setForm({ name: "", category: CATEGORIES[0], price_inr: "", description: "" });
      setImage(null);
      load();
    } catch (err) {
      toast.error(fmtError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleStock = async (p) => {
    try {
      await api.patch(`/products/${p.id}`, { in_stock: !p.in_stock });
      load();
    } catch { toast.error("Could not update stock"); }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/products/${p.id}`, { active: !p.active });
      load();
    } catch { toast.error("Could not update product"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" from the store?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch { toast.error("Could not delete product"); }
  };

  const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-2.5 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

  return (
    <div className="flex flex-col gap-8" data-testid="admin-products">
      <form onSubmit={add} className="border border-white/10 bg-white/[0.02] p-5 sm:p-6 flex flex-col gap-4" data-testid="product-add-form">
        <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 flex items-center gap-2"><Plus size={13} className="text-nx-goldlight" /> Add a product to the store</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <input required maxLength={160} placeholder="Product name" className={input} value={form.name} onChange={set("name")} data-testid="product-name-input" />
          <select className={input} value={form.category} onChange={set("category")} data-testid="product-category-select">
            {CATEGORIES.map((c) => <option key={c} value={c} className="bg-nx-navy text-slate-100">{c}</option>)}
          </select>
          <input type="number" min="1" max="10000000" placeholder="Price in ₹ (empty = price on request)" className={input} value={form.price_inr} onChange={set("price_inr")} data-testid="product-price-input" />
        </div>
        <textarea rows={2} maxLength={2000} placeholder="Short description (optional)" className={`${input} resize-none`} value={form.description} onChange={set("description")} data-testid="product-description-input" />
        <div className="flex flex-wrap items-center gap-4">
          {image ? (
            <span className="flex items-center gap-3 text-sm text-slate-300 border border-nx-gold/40 bg-nx-gold/[0.06] px-4 py-2.5" data-testid="product-image-attached">
              <img src={URL.createObjectURL(image)} alt="Preview" className="w-8 h-8 object-cover border border-white/20" />
              <span className="truncate max-w-40">{image.name}</span>
              <button type="button" onClick={() => setImage(null)} className="text-slate-400 hover:text-white" aria-label="Remove image" data-testid="product-image-remove"><X size={15} /></button>
            </span>
          ) : (
            <label htmlFor="product-image-input" className="flex items-center gap-2 border border-dashed border-white/20 hover:border-nx-gold/50 text-slate-400 hover:text-slate-200 px-4 py-2.5 text-sm cursor-pointer transition-colors" data-testid="product-image-upload">
              <ImagePlus size={15} className="text-nx-goldlight" /> Product photo
              <input id="product-image-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pickImage} />
            </label>
          )}
          <button type="submit" disabled={busy} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-6 py-3 transition-colors disabled:opacity-60 ml-auto" data-testid="product-add-button">
            {busy ? "Adding…" : "Add Product"}
          </button>
        </div>
      </form>

      <div className="flex flex-col gap-3">
        {products.length === 0 && <p className="text-slate-500 text-sm" data-testid="products-empty">No products yet — add your first product above and it appears on the Store page instantly.</p>}
        {products.map((p) => (
          <div key={p.id} className="border border-white/10 bg-white/[0.02] px-5 py-4 flex flex-wrap items-center gap-x-5 gap-y-3" data-testid={`admin-product-${p.id}`}>
            {p.image_path ? (
              <img src={`${API}/products/image/${p.image_path}`} alt="" className="w-12 h-12 object-cover border border-white/10 shrink-0" />
            ) : (
              <span className="w-12 h-12 border border-white/10 flex items-center justify-center shrink-0"><Package size={18} className="text-slate-600" /></span>
            )}
            <div className="min-w-0">
              <p className="text-sm text-white truncate">{p.name} · <span className="text-nx-goldlight">{p.price_inr > 0 ? `₹${p.price_inr.toLocaleString("en-IN")}` : "Price on request"}</span></p>
              <p className="text-xs text-slate-500 truncate">{p.category}</p>
            </div>
            <span className="flex flex-wrap gap-2 ml-auto">
              <button onClick={() => toggleStock(p)} className={`text-[11px] tracking-[0.1em] uppercase border px-3 py-1.5 transition-colors ${p.in_stock ? "text-emerald-300 border-emerald-500/40" : "text-slate-500 border-white/15"}`} data-testid={`product-stock-${p.id}`}>{p.in_stock ? "In Stock" : "Out of Stock"}</button>
              <button onClick={() => toggleActive(p)} className={`text-[11px] tracking-[0.1em] uppercase border px-3 py-1.5 transition-colors ${p.active ? "text-white border-white/20" : "text-slate-500 border-white/15"}`} data-testid={`product-active-${p.id}`}>{p.active ? "Visible" : "Hidden"}</button>
              <button onClick={() => remove(p)} className="text-[11px] tracking-[0.1em] uppercase text-slate-500 hover:text-red-400 border border-white/15 hover:border-red-400/50 px-3 py-1.5 transition-colors" data-testid={`product-delete-${p.id}`}><Trash2 size={12} /></button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
